/**
 * 术语审核面板组件（项目页 / 机翻前阻塞审核）
 *
 * 交互模式（2026-07 重构）："只删除 + 确认剩余全部"
 * - 提取出来的单语术语默认都是要的，PM 只需删掉不要的
 * - PM 删完后点"确认术语列表"，剩余 pending 候选全部变 approved
 * - approved 候选会在机翻启动时注入 prompt 作为"全文术语一致性约束"
 * - 阻塞逻辑不变：有 pending 候选就不能启动机翻
 *
 * 兼容旧版双语候选（targetText 非空的历史数据）。
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { termsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Trash2, Check, Loader2, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'

interface TermCandidate {
  id: string
  projectId: string
  sourceText: string
  targetText?: string  // 新版单语候选为空字符串；旧版双语候选有值
  context?: string
  confidence?: number
  occurrenceCount?: number
  status: string
  createdAt: number
}

interface TermReviewPanelProps {
  projectId: string
  onReviewComplete?: () => void
}

export function TermReviewPanel({ projectId, onReviewComplete }: TermReviewPanelProps) {
  const queryClient = useQueryClient()

  // 获取待审核术语（审批操作会手动 invalidate，轮询 15s 兜底即可）
  const { data: candidatesData, isLoading, error } = useQuery({
    queryKey: ['term-candidates', projectId, 'pending'],
    queryFn: () => termsApi.getCandidates(projectId, 'pending'),
    refetchInterval: 15000,
  })

  const candidates = (candidatesData as any)?.candidates || []

  // 删除单个术语（reject）
  const rejectMutation = useMutation({
    mutationFn: (candidateId: string) => termsApi.rejectCandidate(candidateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['term-candidates', projectId] })
      toast.success('已删除')
      onReviewComplete?.()
    },
    onError: (error: any) => {
      toast.error(error.message || '删除失败')
    }
  })

  // 批量批准剩余全部（PM 确认后，剩余 pending 全部变 approved）
  const batchApproveMutation = useMutation({
    mutationFn: (candidateIds: string[]) => termsApi.batchApproveCandidates(candidateIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['term-candidates', projectId] })
      toast.success(`已确认 ${candidates.length} 个术语，可以启动机翻了`)
      onReviewComplete?.()
    },
    onError: (error: any) => {
      toast.error(error.message || '确认失败')
    }
  })

  if (isLoading) {
    return (
      <Card className="border-warning/30 bg-warning/10">
        <CardContent className="py-8 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-warning" />
          <p className="text-sm text-muted-foreground mt-2">正在加载术语候选...</p>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="border-destructive/50 bg-destructive/5">
        <CardContent className="py-8 text-center">
          <AlertCircle className="h-6 w-6 mx-auto text-destructive" />
          <p className="text-sm text-muted-foreground mt-2">加载术语候选失败</p>
        </CardContent>
      </Card>
    )
  }

  if (candidates.length === 0) {
    return null
  }

  const handleConfirmAll = () => {
    const allIds = candidates.map((c: TermCandidate) => c.id)
    batchApproveMutation.mutate(allIds)
  }

  return (
    <Card className="border-warning/30 bg-warning/10">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-medium">术语确认</CardTitle>
            <Badge variant="outline" className="bg-warning/15 text-warning-foreground border-warning/30">
              {candidates.length} 个待确认
            </Badge>
          </div>
          <Button
            size="sm"
            onClick={handleConfirmAll}
            disabled={batchApproveMutation.isPending}
          >
            {batchApproveMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-1" />
            )}
            确认术语列表
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          以下术语将作为全文一致性约束注入机翻。删掉不需要的，剩余的点"确认术语列表"即可启动机翻。
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {candidates.map((candidate: TermCandidate) => (
          <div
            key={candidate.id}
            className="flex items-start gap-3 p-3 rounded-lg border border-warning/30 bg-white hover:border-warning/40 transition-colors"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-medium text-sm">{candidate.sourceText}</span>
                {candidate.targetText ? (
                  <>
                    <span className="text-muted-foreground">→</span>
                    <span className="font-medium text-sm text-success-foreground">{candidate.targetText}</span>
                  </>
                ) : (
                  <Badge variant="outline" className="text-[10px] h-4 bg-muted/40 text-muted-foreground">
                    单语术语
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {typeof candidate.occurrenceCount === 'number' && (
                  <span>出现 {candidate.occurrenceCount} 次</span>
                )}
              </div>

              {candidate.context && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                  上下文: {candidate.context}
                </p>
              )}
            </div>

            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              onClick={() => rejectMutation.mutate(candidate.id)}
              disabled={rejectMutation.isPending}
              title="删除"
            >
              {rejectMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        ))}

        <p className="text-xs text-muted-foreground mt-3 text-center">
          确认后即可启动机翻
        </p>
      </CardContent>
    </Card>
  )
}
